const OPEN_CLAIM_PREFIX = "airie:video-call:open:";
const OPEN_CLAIM_TTL_MS = 24 * 60 * 60 * 1000;
const tabId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
const locallyClaimedCallKeys = new Set<string>();

type OpenClaim = {
  tabId: string;
  claimedAt: number;
};

function claimKey(videoCallId: number | string): string {
  return `${OPEN_CLAIM_PREFIX}${videoCallId}`;
}

function readClaim(key: string): OpenClaim | null {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) {
      return null;
    }

    const parsed = JSON.parse(value) as Partial<OpenClaim>;
    if (typeof parsed.tabId !== "string" || typeof parsed.claimedAt !== "number") {
      return null;
    }

    return {
      tabId: parsed.tabId,
      claimedAt: parsed.claimedAt,
    };
  } catch {
    // @expected-error-boundary Storage may be unavailable or contain malformed data; treat the call as unclaimed and continue safely.
    return null;
  }
}

function claimCallOpen(videoCallId: number | string): { key: string; value: string } | null {
  const key = claimKey(videoCallId);
  if (locallyClaimedCallKeys.has(key)) {
    return null;
  }

  const existingClaim = readClaim(key);
  if (existingClaim && Date.now() - existingClaim.claimedAt < OPEN_CLAIM_TTL_MS) {
    return null;
  }

  const value = JSON.stringify({ tabId, claimedAt: Date.now() } satisfies OpenClaim);

  try {
    window.localStorage.setItem(key, value);
    if (window.localStorage.getItem(key) !== value) {
      return null;
    }

    locallyClaimedCallKeys.add(key);
    return { key, value };
  } catch {
    // @expected-error-boundary Storage can be blocked by browser privacy settings; fall back to opening in this tab.
    locallyClaimedCallKeys.add(key);
    return { key, value };
  }
}

function releaseClaim(claim: { key: string; value: string }): void {
  locallyClaimedCallKeys.delete(claim.key);

  try {
    if (window.localStorage.getItem(claim.key) === claim.value) {
      window.localStorage.removeItem(claim.key);
    }
  } catch {
    // @expected-error-boundary Storage cleanup is best-effort and must not block opening the call.
  }
}

export function openVideoCallOnce(videoCallId: number | string, url: string): Window | null {
  const claim = claimCallOpen(videoCallId);
  if (!claim) {
    return null;
  }

  const openedWindow = window.open(url, `airie-video-call-${videoCallId}`, "noopener,noreferrer");

  if (!openedWindow) {
    releaseClaim(claim);
  }

  return openedWindow;
}
